import { useMemo } from 'react'
import type { ReadOnlyBodyRenderer } from 'features/load-hub/components/expanded-row/OfferBookEmailCompose'
import { EmailPreviewFrame } from '@/components/tiptap/EmailPreviewFrame'
import { findVisualEditorUnsupportedElements, inspectTemplateHtml } from '../utils/sanitizeTemplateHtml'

/** A designed email at full size swamps the compose pane (a 680px card with a 64px rate). Shown at most at
 *  this fraction — smaller when the pane is narrower — it reads as a preview of the mail; the broker still
 *  receives it at its authored size. */
const COMPOSE_PREVIEW_MAX_SCALE = 0.6

/**
 * The compose body for a template the editor cannot hold — today only a table with column definitions
 * (`<colgroup>`), which the editor's schema does not model. It would show it reshaped and, once the operator typed
 * in it, send the reshaped copy. Everything else ordinary and designed templates carry (tables, logos, rules,
 * headings) opens in the editor. Rendering it read-only shows the broker's email as it will arrive, and the
 * compose sends the template itself, re-interpolated at the current bid.
 */
const ReadOnlyEmailBody = ({ html }: { html: string }) => {
    // Re-sanitized only when the body changes — the drawer re-renders on far more than that.
    const safeHtml = useMemo(() => inspectTemplateHtml(html).sanitizedHtml, [html])
    return (
        <div className="flex flex-col gap-1">
            <EmailPreviewFrame
                title="Email body"
                html={safeHtml}
                maxScale={COMPOSE_PREVIEW_MAX_SCALE}
                className="h-[360px] w-full rounded-lg border border-[var(--lh-border)] bg-white"
            />
            <p className="text-[11px] text-[var(--lh-fg-muted)]">This layout cannot be edited here. Use Edit template to change it.</p>
        </div>
    )
}

/** The compose's read-only body for this board: judged on the RAW template, with the same list the template
 *  editor uses to refuse its visual mode, so the two never disagree about which templates the editor can hold. */
export const READ_ONLY_TEMPLATE_BODY: ReadOnlyBodyRenderer = {
    appliesTo: (templateBody) => findVisualEditorUnsupportedElements(templateBody).length > 0,
    render: (bodyHtml) => <ReadOnlyEmailBody html={bodyHtml} />,
}
